/**
 * NotificationBell Component
 * 
 * Bell icon with unread badge that opens a dropdown panel
 * showing recent notifications.
 * 
 * Features:
 * - Click notification row → navigate to link + mark as read
 * - X button on each row → dismiss (mark read) without navigating
 * - "Mark all read" button → dismiss all
 */

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCircle, X } from "phosphor-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";
import { Button } from "./ui/button";
import { useNotifications } from "../hooks/useNotifications";
import { formatDistanceToNow } from "date-fns";

export default function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const {
    unreadCount,
    notifications,
    loading,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
  } = useNotifications();

  // Fetch notifications when panel opens
  useEffect(() => {
    if (open) {
      // Fetch ONLY unread notifications for the dropdown
      fetchNotifications(10, true);
    }
  }, [open, fetchNotifications]);

  // Handle notification click - navigate + mark as read
  const handleNotificationClick = async (notification) => {
    // Mark as read if not already
    if (!notification.read_at) {
      await markAsRead(notification.notification_id);
    }
    
    // Navigate to the link
    if (notification.link) {
      setOpen(false);
      navigate(notification.link);
    }
  };

  // Handle dismiss X button - mark as read WITHOUT navigating
  const handleDismiss = async (e, notification) => {
    // Prevent row click from firing
    e.stopPropagation();
    
    // Mark as read (removes from unread list)
    if (!notification.read_at) {
      await markAsRead(notification.notification_id);
    }
  };

  // Handle dismiss all
  const handleDismissAll = async () => {
    await markAllAsRead();
  };

  // Format relative time
  const formatTime = (dateString) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch {
      return "";
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="relative p-2 hover:bg-zinc-100 rounded-full transition-colors"
          data-testid="notification-bell"
          aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ""}`}
        >
          <Bell size={22} className="text-zinc-600" weight={unreadCount > 0 ? "fill" : "regular"} />
          {unreadCount > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center text-[10px] font-bold text-white bg-red-500 rounded-full px-1"
              data-testid="notification-badge"
            >
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-80 p-0 shadow-lg"
        data-testid="notification-panel"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-100">
          <h3 className="font-semibold text-sm">Notifications</h3>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="text-xs text-zinc-500 hover:text-zinc-700 h-auto py-1 px-2"
              onClick={handleDismissAll}
              data-testid="dismiss-all-btn"
            >
              <CheckCircle size={14} className="mr-1" />
              Dismiss all
            </Button>
          )}
        </div>

        {/* Notification List */}
        <div className="max-h-[320px] overflow-y-auto">
          {loading && notifications.length === 0 ? (
            <div className="flex items-center justify-center py-8 text-zinc-400">
              <div className="animate-pulse">Loading...</div>
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
              <Bell size={32} className="text-zinc-300 mb-2" />
              <p className="text-sm text-zinc-500">No notifications yet</p>
              <p className="text-xs text-zinc-400 mt-1">
                We&apos;ll notify you when something important happens
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-zinc-100">
              {notifications.map((notification) => (
                <li
                  key={notification.notification_id}
                  className={`group px-4 py-3 hover:bg-zinc-50 cursor-pointer transition-colors ${
                    !notification.read_at ? "bg-blue-50/50" : ""
                  }`}
                  onClick={() => handleNotificationClick(notification)}
                  data-testid={`notification-item-${notification.notification_id}`}
                >
                  <div className="flex items-start gap-3">
                    {/* Unread indicator */}
                    <div className="flex-shrink-0 pt-1.5">
                      {!notification.read_at ? (
                        <div className="w-2 h-2 bg-blue-500 rounded-full" />
                      ) : (
                        <div className="w-2 h-2" />
                      )}
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm ${!notification.read_at ? "font-medium" : "text-zinc-700"}`}>
                        {notification.title}
                      </p>
                      {notification.body && (
                        <p className="text-xs text-zinc-500 mt-0.5 truncate">
                          {notification.body}
                        </p>
                      )}
                      <p className="text-xs text-zinc-400 mt-1">
                        {formatTime(notification.created_at)}
                      </p>
                    </div>

                    {/* Dismiss X button */}
                    <button
                      onClick={(e) => handleDismiss(e, notification)}
                      className="flex-shrink-0 p-1 opacity-0 group-hover:opacity-100 hover:bg-zinc-200 rounded transition-all"
                      aria-label="Dismiss notification"
                      data-testid={`dismiss-notification-${notification.notification_id}`}
                    >
                      <X size={14} className="text-zinc-400 hover:text-zinc-600" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
